import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import AICommandCenter from "@/pages/AICommandCenter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { LayoutGrid, Users, Bot, Filter, Megaphone, DollarSign, TrendingUp, CheckCircle2, ShieldCheck, Star, Ban, Sparkles, Trash2 } from "lucide-react";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "providers", label: "Providers", icon: Users },
  { key: "funnel", label: "Sales Funnel", icon: Filter },
  { key: "ai", label: "AI Command", icon: Bot },
  { key: "marketing", label: "Marketing", icon: Megaphone },
];

const COLORS = ["#16A34A", "#F59E0B", "#15803D", "#D97706", "#84CC16"];

export default function AdminDashboard() {
  const [active, setActive] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [providers, setProviders] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [broadcast, setBroadcast] = useState({ audience: "all", title: "", body: "" });
  const [campaigns, setCampaigns] = useState([]);
  const [genCampaigns, setGenCampaigns] = useState(false);
  const [recovery, setRecovery] = useState(false);

  const load = () => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/providers").then((r) => setProviders(r.data.providers)).catch(() => {});
    api.get("/admin/funnel").then((r) => setFunnel(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setRecovery(!!r.data.abandoned_recovery_enabled)).catch(() => {});
  };
  useEffect(load, []);

  const genSeasonal = async () => {
    setGenCampaigns(true); setCampaigns([]);
    toast.info("AI Marketing Expert is generating city campaigns…");
    try { const r = await api.post("/admin/campaigns/seasonal"); setCampaigns(r.data.campaigns); toast.success(`${r.data.season} campaigns ready for ${r.data.campaigns.length} cities`); }
    catch { toast.error("Generation failed"); } finally { setGenCampaigns(false); }
  };

  const toggleRecovery = async (v) => {
    setRecovery(v);
    try { await api.put(`/admin/settings/recovery?enabled=${v}`); toast.success(v ? "AI Closer recovery enabled" : "Recovery disabled"); }
    catch { toast.error("Failed"); setRecovery(!v); }
  };

  const action = async (pid, act, value) => {
    try { await api.post(`/admin/providers/${pid}/action`, { action: act, value }); toast.success(`Provider ${act}d`); load(); }
    catch { toast.error("Failed"); }
  };

  const [resetting, setResetting] = useState(false);
  const resetDemoData = async () => {
    setResetting(true);
    try {
      const r = await api.post("/admin/reset-demo-data");
      const total = Object.values(r.data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Clean slate! Removed ${total} demo records.`);
      load();
    } catch { toast.error("Reset failed"); } finally { setResetting(false); }
  };

  const sendBroadcast = async () => {
    if (!broadcast.title || !broadcast.body) { toast.error("Fill title and message"); return; }
    try { const r = await api.post("/admin/broadcast", broadcast); toast.success(`Sent to ${r.data.sent} users`); setBroadcast({ audience: "all", title: "", body: "" }); }
    catch { toast.error("Failed"); }
  };

  const rev = metrics?.revenue || {};
  const revData = [
    { name: "Subscription", value: rev.subscription || 0 },
    { name: "Commission", value: rev.commission || 0 },
    { name: "Sponsored", value: rev.sponsored || 0 },
    { name: "Bookings", value: rev.booking || 0 },
    { name: "Affiliate", value: rev.affiliate || 0 },
  ].filter((d) => d.value > 0);

  return (
    <DashboardShell title="Admin Dashboard" nav={NAV} active={active} onNav={setActive} testId="admin-dashboard">
      {active === "overview" && metrics && (
        <div className="space-y-6">
          {/* Revenue hero */}
          <div className="bg-lawn-ink rounded-2xl p-6 sm:p-8 text-white grid md:grid-cols-4 gap-6">
            <div className="md:col-span-1">
              <p className="text-white/50 text-sm">Total Revenue</p>
              <p className="font-display text-4xl font-extrabold text-gold mt-1">${rev.total?.toLocaleString()}</p>
              <p className="text-white/40 text-xs mt-2">Across all streams</p>
            </div>
            <div className="md:col-span-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[["Subscription", rev.subscription], ["Commission", rev.commission], ["Sponsored", rev.sponsored], ["Bookings", rev.booking], ["Affiliate", rev.affiliate]].map(([l, v]) => (
                <div key={l} className="bg-white/5 rounded-xl p-3">
                  <p className="text-[11px] text-white/40">{l}</p>
                  <p className="font-display text-lg font-extrabold">${(v || 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { l: "Customers", v: metrics.users.customers, icon: Users },
              { l: "Providers", v: metrics.users.providers, icon: Users },
              { l: "Active providers", v: metrics.users.active_providers, icon: CheckCircle2 },
              { l: "Paid providers", v: metrics.users.paid_providers, icon: DollarSign },
              { l: "Total bookings", v: metrics.bookings.total, icon: LayoutGrid },
              { l: "Completed", v: metrics.bookings.completed, icon: CheckCircle2 },
              { l: "Abandoned", v: metrics.bookings.abandoned, icon: Ban },
              { l: "Conversion rate", v: `${metrics.kpis.conversion_rate}%`, icon: TrendingUp },
              { l: "Avg booking", v: `$${metrics.kpis.avg_booking_value}`, icon: DollarSign },
              { l: "Customer LTV", v: `$${metrics.kpis.customer_ltv}`, icon: TrendingUp },
              { l: "Provider LTV", v: `$${metrics.kpis.provider_ltv}`, icon: TrendingUp },
              { l: "Upgrade rate", v: `${metrics.kpis.provider_upgrade_rate}%`, icon: Star },
              { l: "Affiliate clicks", v: metrics.kpis.affiliate_clicks, icon: TrendingUp },
              { l: "Affiliate conv.", v: metrics.kpis.affiliate_conversions, icon: CheckCircle2 },
              { l: "Pending providers", v: metrics.users.pending_providers, icon: ShieldCheck },
              { l: "Canceled", v: metrics.bookings.canceled, icon: Ban },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-4">
                <s.icon className="w-4 h-4 text-lawn mb-2" />
                <p className="font-display text-2xl font-extrabold text-lawn-ink">{s.v}</p>
                <p className="text-[11px] text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>

          {/* Charts + tops */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-border rounded-2xl p-6">
              <h3 className="font-display font-bold text-lawn-ink mb-4">Revenue by stream</h3>
              {revData.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={revData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                      {revData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground">No revenue yet.</p>}
            </div>
            <div className="bg-white border border-border rounded-2xl p-6">
              <h3 className="font-display font-bold text-lawn-ink mb-4">Top providers</h3>
              <div className="space-y-2">
                {metrics.top_providers.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-2"><span className="text-xs font-bold text-lawn w-4">{i + 1}</span><span className="text-sm font-semibold text-lawn-ink">{p.name}</span></div>
                    <div className="text-right text-xs text-muted-foreground">⭐ {p.rating} · {p.bookings} jobs · <span className="capitalize text-lawn">{p.tier}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <TopList title="Top cities" items={metrics.top_cities} render={(c) => `${c.name} · ${c.bookings} bookings`} />
            <TopList title="Top services" items={metrics.top_services} render={(c) => `${c.name} · ${c.bookings}`} />
            <TopList title="Top affiliate offers" items={metrics.top_offers} render={(c) => `${c.title} · ${c.clicks} clicks`} />
          </div>
        </div>
      )}

      {active === "providers" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-border rounded-2xl p-4">
            <div>
              <h3 className="font-display font-bold text-lawn-ink">Manage providers</h3>
              <p className="text-sm text-muted-foreground">{providers.length} provider{providers.length === 1 ? "" : "s"} on the platform.</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="reset-demo-btn" variant="outline" className="rounded-full gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="w-4 h-4" /> Reset Demo Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="reset-demo-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Wipe all demo data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes all providers, customers, bookings, quotes, reviews, referrals, payments, notifications, and AI logs — giving you a clean production start. Your admin account, settings, cities, blog posts, and affiliate offers (counters reset) are kept. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="reset-confirm-btn" onClick={resetDemoData} disabled={resetting} className="bg-red-600 hover:bg-red-700">
                    {resetting ? "Wiping…" : "Yes, wipe everything"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <div className="bg-white border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-muted/50 text-left">
                <tr>{["Business", "City", "Tier", "Rating", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-3 font-semibold text-lawn-ink">{h}</th>)}</tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.provider_id} data-testid={`admin-provider-${p.provider_id}`} className="border-t border-border">
                    <td className="px-4 py-3 font-medium text-lawn-ink">{p.business_name}{p.featured && <span className="ml-1 text-gold">★</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.city}</td>
                    <td className="px-4 py-3 capitalize">{p.tier}</td>
                    <td className="px-4 py-3">{p.rating} ({p.review_count})</td>
                    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${p.status === "approved" ? "bg-lawn-light text-lawn-dark" : p.status === "pending" ? "bg-gold-light text-gold-dark" : "bg-red-100 text-red-600"}`}>{p.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {p.status !== "approved" && <Button data-testid={`approve-${p.provider_id}`} size="sm" className="rounded-full h-7 bg-lawn hover:bg-lawn-dark text-xs" onClick={() => action(p.provider_id, "approve")}>Approve</Button>}
                        {!p.verified && <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => action(p.provider_id, "verify")}>Verify</Button>}
                        {!p.featured ? <Button data-testid={`feature-${p.provider_id}`} size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => action(p.provider_id, "feature")}>Feature</Button> : <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => action(p.provider_id, "unfeature")}>Unfeature</Button>}
                        <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs text-red-500" onClick={() => action(p.provider_id, "suspend")}>Suspend</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}

      {active === "funnel" && funnel && (
        <div className="space-y-6">
          <div className="bg-white border border-border rounded-2xl p-6">
            <h3 className="font-display font-bold text-lawn-ink mb-4">Sales funnel</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={funnel.stages} layout="vertical" margin={{ left: 40 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="stage" width={100} tick={{ fontSize: 12 }} />
                <Tooltip /><Bar dataKey="count" fill="#16A34A" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gold-light rounded-2xl p-6"><p className="text-sm text-gold-dark font-semibold">Abandoned bookings</p><p className="font-display text-4xl font-extrabold text-lawn-ink mt-2">{funnel.abandoned_bookings}</p><p className="text-xs text-lawn-ink/60 mt-1">Recoverable with AI Closer follow-ups.</p></div>
            <div className="bg-lawn-light rounded-2xl p-6"><p className="text-sm text-lawn-dark font-semibold">Unconverted quotes</p><p className="font-display text-4xl font-extrabold text-lawn-ink mt-2">{funnel.unconverted_quotes}</p><p className="text-xs text-lawn-ink/60 mt-1">High-interest leads to close.</p></div>
          </div>
        </div>
      )}

      {active === "ai" && <AICommandCenter />}

      {active === "marketing" && (
        <div className="max-w-3xl space-y-6">
          <div className="bg-lawn-ink rounded-2xl p-6 text-white" data-testid="seasonal-campaign-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> Seasonal City Campaigns</h3>
                <p className="text-white/60 text-sm mt-1">AI generates a seasonal promo for each of your top cities.</p>
              </div>
              <Button data-testid="gen-seasonal-btn" onClick={genSeasonal} disabled={genCampaigns} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold shrink-0">{genCampaigns ? "Generating…" : "Generate Campaigns"}</Button>
            </div>
            {campaigns.length > 0 && (
              <div className="mt-4 space-y-3">
                {campaigns.map((c, i) => (
                  <div key={i} data-testid={`campaign-${i}`} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-gold font-semibold text-sm mb-1">{c.city}</p>
                    <p className="text-white/80 text-sm whitespace-pre-line">{c.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-border rounded-2xl p-6 flex items-center justify-between" data-testid="recovery-toggle-card">
            <div>
              <h3 className="font-display font-bold text-lawn-ink flex items-center gap-2"><Bot className="w-5 h-5 text-lawn" /> Abandoned Booking Recovery</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">When on, the AI Closer automatically sends personalized follow-ups (with a 10% comeback offer) to customers who start but don't finish a booking. Runs every 6 hours.</p>
            </div>
            <Switch data-testid="recovery-switch" checked={recovery} onCheckedChange={toggleRecovery} />
          </div>

          <div className="bg-white border border-border rounded-2xl p-6 space-y-4">
            <h3 className="font-display font-bold text-lawn-ink flex items-center gap-2"><Megaphone className="w-5 h-5 text-lawn" /> Send promotional message</h3>
            <div>
              <Label>Audience</Label>
              <Select value={broadcast.audience} onValueChange={(v) => setBroadcast({ ...broadcast, audience: v })}>
                <SelectTrigger data-testid="broadcast-audience" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Everyone</SelectItem><SelectItem value="customers">Customers</SelectItem><SelectItem value="providers">Providers</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Title</Label><Input data-testid="broadcast-title" value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            <div><Label>Message</Label><Textarea data-testid="broadcast-body" value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} rows={4} className="mt-1.5 rounded-xl" /></div>
            <Button data-testid="broadcast-send" onClick={sendBroadcast} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold">Send Broadcast</Button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

const TopList = ({ title, items, render }) => (
  <div className="bg-white border border-border rounded-2xl p-6">
    <h3 className="font-display font-bold text-lawn-ink mb-3">{title}</h3>
    <div className="space-y-2">
      {items?.length ? items.map((it, i) => <p key={i} className="text-sm text-muted-foreground flex items-center gap-2"><span className="text-xs font-bold text-lawn w-4">{i + 1}</span>{render(it)}</p>) : <p className="text-sm text-muted-foreground">No data.</p>}
    </div>
  </div>
);
